/** @module csi/widget/_svg/_AddPageDropDown */
define([
	"dojo/_base/declare",
	"dojo/_base/fx",
	"dojo/dnd/Source",
	"dojo/fx",
	"dijit/_TemplatedMixin",
	"dijit/_WidgetBase",
	"dijit/_WidgetsInTemplateMixin",
	"csi/html",
	"csi/image/_base",
	"csi/locale",
	"csi/printUtil",
	"csi/reflection",
	"csi/xml",
	"./util",
	"./_AddPageDropDownGridMenuItem",
	"./_AddPageDropDownMenuItem",
	"./_AddPageDropDownMenuItemType",
	"dojo/text!./resources/AddPageDropDown.html",
], function(declare, baseFx, DndSource, fx, _TemplatedMixin, _WidgetBase,
			_WidgetsInTemplateMixin, html, csiImage, locale, printUtil, reflection, xml,
			svgUtil, _AddPageDropDownGridMenuItem, _AddPageDropDownMenuItem, _AddPageDropDownMenuItemType, template) {

	return declare([_WidgetBase,_TemplatedMixin,_WidgetsInTemplateMixin], {
		templateString: template,

		// flag to represent results in grid menu are loading
		bResultsLoading: false,

		// if set to true, ignore the first blur event on dropdown
		bShow: false,

		// container node for menu
		menuContainerNode: null,

		// container node for template menu
		templateContainerNode: null,

		// container node for template grid menu
		templateGridContainerNode: null,

		// header node for template menu
		templateMenuHeaderNode: null,

		/**
		 * Toggle grid menu
		 * @param {object} result - show or hide grid menu based on whether or not there is a result
		 */
		toggleGridMenu: async function(result) {
			const {
				editor,
				menuContainerNode,
				templateContainerNode,
				templateGridContainerNode,
				templateMenuHeaderNode,
			} = this;
			const { containerNode } = templateContainerNode;
			const { domNode } = menuContainerNode;
			const { view } = editor;
			const { cacheView } = view;

			// if we have a result, open the grid, otherwise, close it and destroy children
			if (result) {

				// set results loading to true
				if (this.bResultsLoading) {
					return;
				} else {
					this.bResultsLoading = true;
				}

				const portrait = xml.enumGetText(result.PaperOrientation) === "Portrait";
				const top = xml.enumGetText(result.NotesOrientation) === "top";
				const paperSize = xml.enumGetText(result.PaperSize);
				templateMenuHeaderNode.innerText = xml.nodeGetName(result);


				// await page store
				const results = view.templateGridContainerMap[result.$URL] ?? await view.getPagesStore(result).query();

				// iterate over page nodes
				results.forEach((node, index) => {

					// create child grid menu item
					const gridMenuItem = new _AddPageDropDownGridMenuItem({
						onDblClick: () => this._onClickGridMenuItem(node),
					});

					// get main DOM node of grid menu item with destructure
					const { domNode } = gridMenuItem;

					// get main DOM node for image of grid menu item with destructure
					const { imageNode } = gridMenuItem;

					// get main DOM node style of grid menu
					const { style } = domNode;

					// get ratio for height, default to A4
					const RATIO = printUtil.getRatioOfPaperSizeAndOrientation(paperSize, portrait);

					// get height based on width and ratio, width is always 96px
					let height = RATIO*96;

					const HUNDRED_PERCENT = "100%";
					// get image node style and adjust to orientation
					const imageStyle = imageNode.style;
					if (top) {
						imageStyle.maxHeight = imageStyle.width = HUNDRED_PERCENT;

						// for top / bottom notes orientation, subtract 7px for splitter
						height -= 7;
					} else {
						imageStyle.maxWidth = imageStyle.height = HUNDRED_PERCENT;
					}

					// adjust to page orientation
					html.setPixels(domNode, html.HEIGHT, height);

					// get snapshot node
					const snapshotNode = cacheView.getNode(node.Snapshot);

					// set function to handle image errors
					imageNode.onerror = () => {
						// hide the default error image
						html.setVisible(imageNode, false);

						/* 
							C8-27322
							csiImage.isError(node) is true for null URLs
							(e.g. .../WebAccess/null) and false for real URLs
							(e.g. .../csi-requesthandler/...). Since the error on 
							imageNode was triggered, we can use this function to detect
							whether the canvas is blank or has an error. For images that
							are blank, the thumbnail is also blank. For images that have a
							real URL that does not exist, the image is a custom SVG designed by UX.
						*/
						if (!csiImage.isError(snapshotNode)) {
							// add custom icon by adding the appropriate classes to the parent element class
							imageNode.parentElement.classList.add("csiActionIcon", "iconSaveTemplate");

							// center image by removing width of img, displaying flex, and justifying content of container
							style.display = "flex";
							style.justifyContent = "center";
							style.alignItems = "center";
							style.backgroundRepeat = "no-repeat";

							// set image style, and force height to 85%
							imageStyle.display = "none";

							/*

								Note: The reason we are not centering normal
								images is that we want the thumbnail to accurately
								represent where the note's slider is in each particular
								canvas page. For example, if we have a canvas page with the
								notes slider to half of the page content (where X's are page
								content, and O's represent notes pane), it would look like this:

								Left / Right Notes Orientation:
								_________________
								|XXXXXXXOOOOOOOO|
								|XXXXXXXOOOOOOOO|
								|XXXXXXXOOOOOOOO|
								|XXXXXXXOOOOOOOO|
								-----------------

								Top / Bottom Notes Orientation:
								_________________
								|XXXXXXXXXXXXXXX|
								|XXXXXXXXXXXXXXX|
								|OOOOOOOOOOOOOOO|
								|OOOOOOOOOOOOOOO|
								-----------------

							*/
						}
					}

					// get and set image src to image node
					imageNode.src = csiImage.getImageSrc(snapshotNode, null, reflection.ATTR_MEDIUM_IMAGE);

					// setup ids for dnd
					const gridText = "grid-menu-item-" + index;
					domNode.id = gridText;
					domNode.nodeId = node.$URL;

					// append nodes to dom
					templateGridContainerNode.addChild(gridMenuItem);

					// setup dnd source on gridMenuItem
					const gridDnd = new DndSource(gridMenuItem, {
						accept: [svgUtil.DND_TYPE_IMAGE],
						dropParent: templateGridContainerNode.domNode,
						grid: { dndType: "template" },
					});

					// setup items for dnd
					gridDnd.setItem(gridText, { type: [svgUtil.DND_TYPE_IMAGE] });
				});

				// save results
				view.templateGridContainerMap[result.$URL] = results;

				this.bResultsLoading = false;

				this._animateMenuSwap(containerNode, domNode, true);
			} else {
				this._animateMenuSwap(domNode, containerNode, false);
				templateGridContainerNode.destroyDescendants();
			}
		},

		/**
		 * Animate the swap of menus, where showNode is the node to show, and hideNode is the node to hide
		 * @param { HTMLElement } nodeToShow
		 * @param { HTMLElement } nodeToHide
		 * @param { boolean } blockNextBlur
		 */
		_animateMenuSwap(nodeToShow, nodeToHide, blockNextBlur) {
			html.setVisible(nodeToShow, true);
			fx.combine([
				baseFx.fadeOut({
					duration: 250,
					node: nodeToHide,
					onEnd: () => {
						html.setVisible(nodeToHide, false);
						// set bShow to true on parent node, since we will lose focus here
						this.bShow = blockNextBlur;
					}
				}),
				baseFx.fadeIn({
					duration: 250,
					node: nodeToShow,
				})
			]).play();
		},

		/**
		 * When clicking the grid menu header, go back to the main menu
		 */
		_onClickGridMenuHeader: function() {
			this.toggleGridMenu();
		},

		/**
		 * When double clicking a grid menu item, add that page from template
		 */
		_onClickGridMenuItem: function(node) {
			// keep dropdown open after double click
			this.bShow = true;

			const { view } = this.editor;
			if (view.hasMaxPages()) {
				// show max pages reached dialog
				svgUtil.popupMaxPageErrorMessage();
			} else {
				view.addPageFromTemplate(node);
			}
		},

		/**
		 * Rebuild the dropdown.
		 */
		async buildDropdown() {
			const {
				editor,
				menuContainerNode,
				templateContainerNode,
			} = this;
			menuContainerNode.destroyDescendants();
			const results = await editor.view.canvasTemplateStore.query();
			if (results && results.length) {
				const typeResults = _.uniq(results.map(result => xml.enumGetText(result.DatasheetType)));
				typeResults.forEach((type) => {
					const typeResult = type === "" ? locale.resource("General") : locale.type(type);
					menuContainerNode.addChild(new _AddPageDropDownMenuItemType({
						label: typeResult,
					}));
					const resultsForType = results.filter(result => xml.enumGetText(result.DatasheetType) === type);
					resultsForType.forEach((result) => {
						menuContainerNode.addChild(new _AddPageDropDownMenuItem({
							label: xml.nodeGetName(result),
							menuContainerNode,
							result,
							templateContainerNode,
							onClick: () => this.toggleGridMenu(result),
						}));
					});
				});
			} else {
				menuContainerNode.addChild(new _AddPageDropDownMenuItemType({
					label: locale.resource("SEARCH_NO_RESULTS"),
				}));
			}
		},
	});
});
